/**
 * Inline formatting for short fields — key/value cells and the advisor description.
 *
 * SQUAD asked for two things on these: a line break they place themselves
 * ("Manuellen Umbruch ermöglichen") and the odd word in bold, so a status like
 * "Softclose" stands out from the explanation after it.
 *
 * The notation is the one the repo already uses for long text in
 * squadRichText.plainTextToRichHtml: a newline breaks the line, `**…**` sets bold.
 * Reusing it keeps one convention across the template rather than teaching the
 * customer a second one for short fields. The long-text path stays on its HTML
 * pipeline — these fields are plain strings in a plain text box, and giving each
 * of twenty table rows a rich-text editor would be worse than the notation.
 */

export interface SquadInlineSegment {
  text: string;
  bold: boolean;
}

/** One entry per line; each line is a list of segments. */
export type SquadInlineLine = SquadInlineSegment[];

const BOLD = /\*\*([\s\S]+?)\*\*/g;

/**
 * Split one line into bold and plain segments.
 * An unclosed `**` is left as literal text — the customer is mid-typing, not asking
 * for the rest of the field to turn bold.
 */
function parseLine(line: string): SquadInlineLine {
  const segments: SquadInlineSegment[] = [];
  let cursor = 0;
  BOLD.lastIndex = 0;
  let match = BOLD.exec(line);
  while (match) {
    if (match.index > cursor) {
      segments.push({ text: line.slice(cursor, match.index), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    cursor = match.index + match[0].length;
    match = BOLD.exec(line);
  }
  if (cursor < line.length) segments.push({ text: line.slice(cursor), bold: false });
  return segments.length ? segments : [{ text: '', bold: false }];
}

export function parseSquadInlineText(value: unknown): SquadInlineLine[] {
  const raw = value == null ? '' : String(value);
  if (!raw) return [];
  return raw.split('\n').map(parseLine);
}

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Same notation rendered for the web preview. It exists so preview and PDF read the
 * one parser: the preview used to run its own regex, which did not match a bold span
 * across a line break and so disagreed with the page the customer gets.
 */
export function squadInlineTextToHtml(value: unknown): string {
  return parseSquadInlineText(value)
    .map((line) =>
      line
        .map((segment) =>
          segment.bold ? `<b>${escapeHtml(segment.text)}</b>` : escapeHtml(segment.text),
        )
        .join(''),
    )
    .join('<br/>');
}
